/**
 * Server-sent events over fetch (EventSource cannot send the Authorization header).
 * Reconnects with Last-Event-ID until `signal` aborts or `onEvent` returns 'stop'.
 */
export interface SseEvent {
  id?: string;
  event: string;
  data: string;
}

export function parseSse(buffer: string): { events: SseEvent[]; rest: string } {
  const events: SseEvent[] = [];
  const blocks = buffer.replace(/\r\n/g, '\n').split('\n\n');
  const rest = blocks.pop() ?? '';
  for (const block of blocks) {
    let event = 'message';
    let id: string | undefined;
    const data: string[] = [];
    for (const line of block.split('\n')) {
      if (!line || line.startsWith(':')) continue;
      const i = line.indexOf(':');
      const field = i < 0 ? line : line.slice(0, i);
      const value = i < 0 ? '' : line.slice(i + 1).replace(/^ /, '');
      if (field === 'event') event = value;
      else if (field === 'data') data.push(value);
      else if (field === 'id') id = value;
    }
    if (data.length) events.push({ id, event, data: data.join('\n') });
  }
  return { events, rest };
}

export async function readSse(
  url: string,
  headers: () => Promise<Record<string, string>>,
  onEvent: (e: SseEvent) => 'stop' | undefined,
  signal: AbortSignal,
  fetchImpl: typeof fetch = (...a) => fetch(...a),
): Promise<void> {
  let lastId: string | undefined;
  for (let attempt = 0; attempt < 6 && !signal.aborted; attempt++) {
    const h = await headers();
    if (lastId) h['Last-Event-ID'] = lastId;
    h.Accept = 'text/event-stream';
    const res = await fetchImpl(url, { headers: h, signal });
    if (!res.ok || !res.body) throw new Error(`Event stream failed (HTTP ${res.status}).`);
    const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
    let buf = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += value;
      const { events, rest } = parseSse(buf);
      buf = rest;
      for (const e of events) {
        if (e.id) lastId = e.id;
        if (onEvent(e) === 'stop') {
          await reader.cancel().catch(() => {});
          return;
        }
      }
    }
    await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
  }
}
